import { shuffleChoices } from '@util/FlowUtils';
import React, { FunctionComponent, useMemo, useState } from 'react';
import { isEmpty, isNonEmptyString } from 'ts-utils';
import { RadioButton, RadioGroup } from '../../functional/OptionRadioButton';
import { QuestionWrapper } from '../../styles/Containers';
import { CopyContainer } from '../../styles/CopyContainer';
import { InfoBox } from '../../styles/InfoBox';
import { autoWidth } from '../../styles/OptionButton';
import { BodySM, Heading3 } from '../../styles/Typography';

export type ChoiceItem =
  | string
  | {
      value: string;
      label: string;
    };

export type SelectLayoutConfig = {
  heading?: string;
  body?: string[];
  bodyAlign?: 'left' | 'center';
  choices: ChoiceItem[];
  randomizeChoices?: boolean;
  animationEnabled?: boolean;
  toastMessage?: string;
  toastDuration?: number;
  infoBox?: {
    titleText?: string;
    bodyText: string;
  };
};

type Props = SelectLayoutConfig & {
  onSelected?: (value: string) => void;
  noWrapper?: boolean;
  onShowToast?: () => void;
};

export const SingleSelectLayout: FunctionComponent<Props> = (props) => {
  const {
    heading,
    body,
    bodyAlign,
    choices: unshuffledChoices,
    randomizeChoices,
    onSelected,
    noWrapper,
    animationEnabled = true,
    toastMessage,
    onShowToast,
    infoBox,
  } = props;

  const choices = useMemo(
    () => (randomizeChoices ? shuffleChoices(unshuffledChoices) : unshuffledChoices),
    []
  );
  const [selected, setSelected] = useState<string>();

  function onSelectionChange(selectedValue: string) {
    setSelected(selectedValue);
    if (isNonEmptyString(toastMessage)) {
      onShowToast?.();
    }
    onSelected?.(selectedValue);
  }

  const content = (
    <div style={{ margin: 'auto' }}>
      <CopyContainer style={bodyAlign === undefined ? undefined : { textAlign: bodyAlign }}>
        {!isEmpty(heading) && <Heading3>{heading}</Heading3>}
        {body?.map((text: string, index: number) => {
          return (
            <BodySM key={index} data-test="surveyQuestion">
              {text}
            </BodySM>
          );
        })}
      </CopyContainer>
      <RadioGroup defaultSelected={selected} onChange={onSelectionChange}>
        {choices.map((choice: ChoiceItem, index: number) => {
          const label = typeof choice === 'object' ? choice.label : choice;
          const value = typeof choice === 'object' ? choice.value : choice;

          return (
            <RadioButton
              key={index}
              width={autoWidth(choices)}
              value={value}
              animationEnabled={animationEnabled}
              animationIndex={index}
            >
              {label}
            </RadioButton>
          );
        })}
      </RadioGroup>
      {infoBox && <InfoBox {...infoBox} />}
    </div>
  );

  return <>{noWrapper ? content : <QuestionWrapper>{content}</QuestionWrapper>}</>;
};
